import React from 'react';
import config from '@plone/volto/registry';
import { flattenToAppURL } from '@plone/volto/helpers';
import useEmblaCarousel from 'embla-carousel-react';
import './multicard.css';

const getImageURL = (image) => {
  if (!image) return null;
  if (typeof image === 'string') {
    return `${flattenToAppURL(image)}/@@images/image/large`;
  }
  const imageItem = Array.isArray(image) ? image[0] : image;
  if (imageItem?.['@id']) return `${flattenToAppURL(imageItem['@id'])}/@@images/image/large`;
  if (imageItem?.download) return flattenToAppURL(imageItem.download);
  return null;
};

const getHref = (url, anchor) => {
  const base = typeof url === 'string' ? url : (Array.isArray(url) ? url[0]?.['@id'] : url?.['@id']);
  return anchor?.trim() ? (base ? `${base}${anchor}` : anchor) : base || '#';
};

const isExternalLink = (href) => {
  if (!href) return false;
  if (href.startsWith('/')) return false;
  if (config?.settings?.apiPath && href.startsWith(config.settings.apiPath)) {
    return false;
  }
  return href.startsWith('http');
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const Card = ({ item }) => {
  const imageUrl = getImageURL(item.image);
  const href = getHref(item.url, item.anchor);
  const external = isExternalLink(href);

  return (
    <div
      className="multi-card"
      style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}
    >
      <div className="multi-card-overlay" />
      <div className="multi-card-content">
        {item.title?.trim() && <h3>{item.title}</h3>}
        {item.description?.trim() && <p>{item.description}</p>}
        {item.linkText?.trim() && (
          <a
            href={href}
            target={external ? '_blank' : '_self'}
            rel={external ? 'noopener noreferrer' : undefined}
          >
            {item.linkText}
          </a>
        )}
      </div>
    </div>
  );
};

const View = ({ data = {} }) => {
  const items = data.items || [];
  const isMobile = useIsMobile(768);
  const shouldCarousel = data.mobileCarousel && isMobile && items.length > 1;

  // Pull carousel defaults from block config, mirroring Icon Link Row
  const { autoplay = true, delay = 3500 } =
    config?.blocks?.blocksConfig?.multiCard?.carousel || {};

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: 'trimSnaps',
      dragFree: false,
      slidesToScroll: 1,
    },
    [],
  );

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [snapCount, setSnapCount] = React.useState(0);

  React.useEffect(() => {
    if (!shouldCarousel || !emblaApi || !autoplay) return;
    const id = setInterval(() => emblaApi?.scrollNext(), delay);
    return () => clearInterval(id);
  }, [shouldCarousel, emblaApi, autoplay, delay]);

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  React.useEffect(() => {
    if (!emblaApi) return;
    setSnapCount(emblaApi.scrollSnapList().length);
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  if (!shouldCarousel) {
    return (
      <div className="multi-card-outer">
        <div className={`multi-card-container align-${data.alignment || 'left'} columns-${data.columns || 3}`}>
          {items.map((item, index) => (
            <Card key={index} item={item} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="multi-card-outer">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {items.map((item, index) => (
            <div className="embla__slide" key={index}>
              <Card item={item} />
            </div>
          ))}
        </div>
        <div className="embla__dots">
          {Array.from({ length: snapCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`embla__dot ${selectedIndex === i ? 'is-selected' : ''}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default View;
